import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleDropdownToggle = (dropdown) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  return (
    <>
      {/* Skip Navigation Link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-[#630503] focus:text-white focus:px-4 focus:py-2 focus:rounded-md focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-white"
      >
        Skip to main content
      </a>

    <header
      // Real, opaque backdrop when not scrolled: a solid brand-maroon plate,
      // not the page's white background and not a translucent one either
      // (a translucent background still depends on whatever photo/video is
      // playing underneath, which is exactly what made the old contrast
      // check indeterminate). White nav text on this solid color is a
      // verifiable >=4.5:1 in every browser, every time.
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled
        ? 'bg-white/95 backdrop-blur-md shadow-lg'
        : 'bg-[#4a0402]'
    }`}>
      {/* Main navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img
                src="/assets/images/navbar/PS 11 Mascot 3.png"
                alt="P.S. 11X The Highbridge School Logo"
                className="h-16 w-auto transition-all duration-300"
              />
            </Link>
          </div>

          {/* Desktop navigation */}
          <nav className="hidden lg:flex items-center space-x-6" aria-label="Main navigation">
            
            {/* Home Link */}
            <Link 
              to="/" 
              className={`font-medium text-base transition-colors duration-300 ${
                isScrolled 
                  ? 'text-gray-700 hover:text-gray-900' 
                  : 'text-white/90 hover:text-white'
              }`}
            >
              HOME
            </Link>
            
            {/* Our School Dropdown */}
            <div className="relative group">
              <button
                className={`font-medium flex items-center text-base transition-colors duration-300 ${
                  isScrolled
                    ? 'text-gray-700 hover:text-gray-900'
                    : 'text-white/90 hover:text-white'
                }`}
                onClick={() => handleDropdownToggle('our-school')}
                aria-expanded={activeDropdown === 'our-school'}
                aria-haspopup="true"
                aria-label="Our School menu"
              >
                OUR SCHOOL
                <svg className="w-4 h-4 ml-1" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              
              {/* Our School Dropdown */}
              <div className={`absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border transition-all duration-200 ${
                activeDropdown === 'our-school' ? 'opacity-100 visible' : 'opacity-0 invisible'
              }`}>
                <div className="py-2">
                  <Link to="/about-us" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 font-medium">ABOUT US</Link>
                  <Link to="/mission-vision" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 font-medium">MISSION & VISION STATEMENT</Link>
                  <Link to="/history" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 font-medium">HISTORY</Link>
                  <Link to="/principal-message" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 font-medium">PRINCIPAL'S MESSAGE</Link>
                  <Link to="/our-buildings" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 font-medium">OUR 2 BUILDINGS</Link>
                </div>
              </div>
            </div>
            
            {/* Academics Dropdown */}
            <div className="relative group">
              <button
                className={`font-medium flex items-center text-base transition-colors duration-300 ${
                  isScrolled
                    ? 'text-gray-700 hover:text-gray-900'
                    : 'text-white/90 hover:text-white'
                }`}
                onClick={() => handleDropdownToggle('academics')}
                aria-expanded={activeDropdown === 'academics'}
                aria-haspopup="true"
                aria-label="Academics menu"
              >
                ACADEMICS
                <svg className="w-4 h-4 ml-1" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              
              {/* Academics Dropdown */}
              <div className={`absolute top-full left-0 mt-2 w-52 bg-white rounded-lg shadow-lg border transition-all duration-200 ${
                activeDropdown === 'academics' ? 'opacity-100 visible' : 'opacity-0 invisible'
              }`}>
                <div className="py-2">
                  <Link to="/learning-resources" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 font-medium">LEARNING RESOURCES</Link>
                  <Link to="/classes" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 font-medium">CLASSES</Link>
                </div>
              </div>
            </div>
            
            {/* Faculty Dropdown */}
            <div className="relative group">
              <button
                className={`font-medium flex items-center text-base transition-colors duration-300 ${
                  isScrolled
                    ? 'text-gray-700 hover:text-gray-900'
                    : 'text-white/90 hover:text-white'
                }`}
                onClick={() => handleDropdownToggle('faculty')}
                aria-expanded={activeDropdown === 'faculty'}
                aria-haspopup="true"
                aria-label="Faculty menu"
              >
                FACULTY
                <svg className="w-4 h-4 ml-1" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              
              {/* Faculty Dropdown */}
              <div className={`absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border transition-all duration-200 ${
                activeDropdown === 'faculty' ? 'opacity-100 visible' : 'opacity-0 invisible'
              }`}>
                <div className="py-2">
                  <Link to="/staff" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 font-medium">STAFF DIRECTORY</Link>
                  <Link to="/meet-the-mascot" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 font-medium">MEET THE MASCOT</Link>
                  <Link to="/teacher-login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 font-medium">TEACHER LOGIN</Link>
                  <a 
                    href="https://forms.gle/dpw8HgiggLsxr7UV8" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 font-medium"
                  >
                    TECHNOLOGY
                  </a>
                </div>
              </div>
            </div>
            
            {/* Enrichment Dropdown */}
            <div className="relative group">
              <button
                className={`font-medium flex items-center text-base transition-colors duration-300 ${
                  isScrolled
                    ? 'text-gray-700 hover:text-gray-900'
                    : 'text-white/90 hover:text-white'
                }`}
                onClick={() => handleDropdownToggle('enrichment')}
                aria-expanded={activeDropdown === 'enrichment'}
                aria-haspopup="true"
                aria-label="Enrichment menu"
              >
                ENRICHMENT
                <svg className="w-4 h-4 ml-1" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              
              {/* Enrichment Dropdown */}
              <div className={`absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border transition-all duration-200 ${
                activeDropdown === 'enrichment' ? 'opacity-100 visible' : 'opacity-0 invisible'
              }`}>
                <div className="py-2">
                  <Link to="/library" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 font-medium">LIBRARY</Link>
                  <Link to="/gym" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 font-medium">GYM</Link>
                  <Link to="/art" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 font-medium">ART</Link>
                </div>
              </div>
            </div>
            
            {/* PBIS */}
            <Link to="/pbis" className={`font-medium text-base transition-colors duration-300 ${
              isScrolled 
                ? 'text-gray-700 hover:text-gray-900' 
                : 'text-white/90 hover:text-white'
            }`}>PBIS</Link>
            
            {/* Community Events */}
            <Link to="/community-events" className={`font-medium text-base transition-colors duration-300 ${
              isScrolled 
                ? 'text-gray-700 hover:text-gray-900' 
                : 'text-white/90 hover:text-white'
            }`}>COMMUNITY EVENTS</Link>
            
            {/* Gallery */}
            <Link to="/gallery" className={`font-medium text-base transition-colors duration-300 ${
              isScrolled
                ? 'text-gray-700 hover:text-gray-900'
                : 'text-white/90 hover:text-white'
            }`}>GALLERY</Link>
          </nav>

          {/* Utility controls: language selector (all widths) + mobile menu button (mobile only) */}
          <div className="flex items-center gap-3">
            <span id="site-language" className="inline-flex items-center"></span>
            <div className="lg:hidden flex items-center">
              <button
                className={`inline-flex items-center justify-center p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-inset transition-colors duration-300 ${
                  isScrolled
                    ? 'text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:ring-gray-500'
                    : 'text-white/90 hover:text-white hover:bg-white/10 focus:ring-white/50'
                }`}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-expanded={isMobileMenuOpen}
                aria-label={isMobileMenuOpen ? "Close main menu" : "Open main menu"}
              >
                <span className="sr-only">{isMobileMenuOpen ? "Close main menu" : "Open main menu"}</span>
                {isMobileMenuOpen ? (
                  <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`lg:hidden ${isMobileMenuOpen ? 'block' : 'hidden'}`}>
        <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t max-h-[80vh] overflow-y-auto">
          {/* Mobile Home Link */}
          <Link 
            to="/" 
            className="block px-3 py-2 text-base font-medium text-gray-900 rounded-md hover:bg-gray-50"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            HOME
          </Link>
          
          {/* Mobile Our School Section */}
          <div className="border-b border-gray-200 pb-2 mb-2">
            <div className="px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">OUR SCHOOL</div>
            <Link to="/about-us" className="block px-6 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)}>About Us</Link>
            <Link to="/mission-vision" className="block px-6 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)}>Mission & Vision Statement</Link>
            <Link to="/history" className="block px-6 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)}>History</Link>
            <Link to="/principal-message" className="block px-6 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)}>Principal's Message</Link>
            <Link to="/our-buildings" className="block px-6 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)}>Our 2 Buildings</Link>
          </div>
          
          {/* Mobile Academics Section */}
          <div className="border-b border-gray-200 pb-2 mb-2">
            <div className="px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">ACADEMICS</div>
            <Link to="/learning-resources" className="block px-6 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)}>Learning Resources</Link>
            <Link to="/classes" className="block px-6 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)}>Classes</Link>
          </div>
          
          {/* Mobile Faculty Section */}
          <div className="border-b border-gray-200 pb-2 mb-2">
            <div className="px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">FACULTY</div>
            <Link to="/staff" className="block px-6 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)}>Staff Directory</Link>
            <Link to="/meet-the-mascot" className="block px-6 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)}>Meet the Mascot</Link>
            <Link to="/teacher-login" className="block px-6 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)}>Teacher Login</Link>
            <a 
              href="https://forms.gle/dpw8HgiggLsxr7UV8" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="block px-6 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Technology
            </a>
          </div>
          
          {/* Mobile Enrichment Section */}
          <div className="border-b border-gray-200 pb-2 mb-2">
            <div className="px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">ENRICHMENT</div>
            <Link to="/library" className="block px-6 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)}>Library</Link>
            <Link to="/gym" className="block px-6 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)}>Gym</Link>
            <Link to="/art" className="block px-6 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)}>Art</Link>
          </div>
          
          {/* Mobile PBIS */}
          <Link to="/pbis" className="block px-3 py-2 text-base font-medium text-gray-700 rounded-md hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)}>PBIS</Link>
          
          {/* Mobile Community Events */}
          <Link to="/community-events" className="block px-3 py-2 text-base font-medium text-gray-700 rounded-md hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)}>COMMUNITY EVENTS</Link>
          
          {/* Mobile Gallery */}
          <Link to="/gallery" className="block px-3 py-2 text-base font-medium text-gray-700 rounded-md hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)}>GALLERY</Link>
        </div>
      </div>
    </header>
    </>
  );
};

export default Navbar; 